import {
  createContext,
  forwardRef,
  useContext,
  useId,
  useLayoutEffect,
  useMemo,
  useState,
  type AriaAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { Icon } from '../Icon';
import { cx } from './cx';

// Rediseño · Bloque A. `Field` pone la etiqueta, la ayuda y el error, y los enlaza con el control que
// envuelve; `Input`, `Select` y `Textarea` son ese control. Juntos resuelven lo que cada formulario
// repetía a mano: que la etiqueta apunte al campo, que la ayuda y el error se lean al enfocarlo y que
// un campo con error lo diga con algo más que el color.

interface FieldContextValue {
  controlId: string;
  /** El control avisa de su `id` propio para que la etiqueta apunte a él (`null` lo retira). */
  adoptControlId: (id: string | null) => void;
  describedBy: string | undefined;
  invalid: boolean;
  required: boolean;
}

const FieldContext = createContext<FieldContextValue | null>(null);

export interface FieldProps {
  label: ReactNode;
  /** Ayuda permanente bajo el campo. */
  hint?: ReactNode;
  /** Mensaje de error. Si existe, el campo se marca como inválido y el mensaje se anuncia. */
  error?: ReactNode;
  required?: boolean;
  className?: string;
  /** Un solo control: `Input`, `Select` o `Textarea`. */
  children: ReactNode;
}

export function Field({ label, hint, error, required = false, className, children }: FieldProps) {
  const id = useId();
  // La etiqueta apunta al id del control. Normalmente es uno generado; si el control trae el suyo
  // (`<Input id="precio" />`), se adopta ese: si no, la etiqueta apuntaría a un id que nadie lleva.
  const [adoptedId, adoptControlId] = useState<string | null>(null);
  const controlId = adoptedId ?? `${id}-control`;
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const invalid = Boolean(error);
  // El error va primero: es lo que importa oír antes que la ayuda.
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;
  const context = useMemo(
    () => ({ controlId, adoptControlId, describedBy, invalid, required }),
    [controlId, describedBy, invalid, required],
  );

  return (
    <div className={cx('n3-field', className)}>
      <label className="n3-field__label" htmlFor={controlId}>
        {label}
        {required && <span aria-hidden="true" className="n3-field__required"> *</span>}
      </label>
      <FieldContext.Provider value={context}>{children}</FieldContext.Provider>
      {hint && <p className="n3-field__hint" id={hintId}>{hint}</p>}
      {error && (
        <p className="n3-field__error" id={errorId} role="alert">
          <Icon name="warning" size={16} />
          <span>{error}</span>
        </p>
      )}
    </div>
  );
}

interface ControlProps {
  id?: string;
  required?: boolean;
  invalid?: boolean;
  'aria-describedby'?: string;
  'aria-invalid'?: AriaAttributes['aria-invalid'];
}

/**
 * Lo que el control hereda de su `Field`. Fuera de un `Field` no hereda nada y se comporta como un
 * control suelto: quien lo use así pone su propia etiqueta (`aria-label` o un `<label>`).
 *
 * Quién decide si el campo es inválido, de más a menos: el `aria-invalid` que se le pase al control,
 * su prop `invalid`, y el `error` del `Field`. Lo que se escribe a mano manda sobre lo derivado, y lo
 * que se anuncia es lo mismo que se ve.
 */
function useFieldControl(props: ControlProps) {
  const field = useContext(FieldContext);

  const ownId = props.id;
  const adoptControlId = field?.adoptControlId;
  useLayoutEffect(() => {
    if (!adoptControlId || !ownId) return undefined;
    adoptControlId(ownId);
    return () => adoptControlId(null);
  }, [adoptControlId, ownId]);

  const explicit = props['aria-invalid'];
  const explicitInvalid = explicit === undefined ? undefined : explicit !== false && explicit !== 'false';
  const invalid = explicitInvalid ?? props.invalid ?? field?.invalid ?? false;
  return {
    invalid,
    control: {
      id: ownId ?? field?.controlId,
      required: props.required ?? field?.required,
      'aria-describedby': [props['aria-describedby'], field?.describedBy].filter(Boolean).join(' ') || undefined,
      'aria-invalid': explicit ?? (invalid || undefined),
    },
  };
}

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
  /** Lo que va pegado antes del valor («$»). */
  prefix?: ReactNode;
  /** Lo que va pegado después del valor («%», «kg»). */
  suffix?: ReactNode;
  /** Solo hace falta fuera de un `Field`; dentro, lo decide el `error` del `Field`. */
  invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { prefix, suffix, invalid, className, id, required, disabled, 'aria-describedby': describedBy, 'aria-invalid': ariaInvalid, ...rest },
  ref,
) {
  const field = useFieldControl({ id, required, invalid, 'aria-describedby': describedBy, 'aria-invalid': ariaInvalid });
  return (
    <span className={cx('n3-input', field.invalid && 'n3-input--invalid', disabled && 'n3-input--disabled', className)}>
      {prefix && <span className="n3-input__affix n3-input__affix--start">{prefix}</span>}
      <input {...rest} {...field.control} className="n3-input__control" disabled={disabled} ref={ref} />
      {suffix && <span className="n3-input__affix n3-input__affix--end">{suffix}</span>}
    </span>
  );
});

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { invalid, className, id, required, disabled, 'aria-describedby': describedBy, 'aria-invalid': ariaInvalid, children, ...rest },
  ref,
) {
  const field = useFieldControl({ id, required, invalid, 'aria-describedby': describedBy, 'aria-invalid': ariaInvalid });
  return (
    <span className={cx('n3-input', 'n3-input--select', field.invalid && 'n3-input--invalid', disabled && 'n3-input--disabled', className)}>
      <select {...rest} {...field.control} className="n3-input__control" disabled={disabled} ref={ref}>
        {children}
      </select>
      <span className="n3-input__chevron"><Icon name="chevron-down" size={18} /></span>
    </span>
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { invalid, className, id, required, disabled, 'aria-describedby': describedBy, 'aria-invalid': ariaInvalid, ...rest },
  ref,
) {
  const field = useFieldControl({ id, required, invalid, 'aria-describedby': describedBy, 'aria-invalid': ariaInvalid });
  return (
    <span className={cx('n3-input', 'n3-input--multiline', field.invalid && 'n3-input--invalid', disabled && 'n3-input--disabled', className)}>
      <textarea {...rest} {...field.control} className="n3-input__control" disabled={disabled} ref={ref} />
    </span>
  );
});
